//=================================================
// Button Component
// A flexible,accessible button with multiple varient
//=================================================

import React, { forwardRef } from "react";
import clsx from "clsx";
import styles from "./Button.module.scss";
import type { ButtonProps } from "./Button.types";

// ============================================
// LOADING SPINNER COMPONENT
// Simple animated spinner for loading state
// ============================================
interface LoadingSpinnerProps {
  className?: string;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = (
  { className }, // SVG spinner with two parts:
) => (
  // 1. Track (full circle, dimmed)
  // 2. Head (partial circle, animated)
  <svg
    className={clsx(styles.spinner, className)}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Track - the background circle */}
    <circle
      className={styles.spinnerTrack}
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="3"
    />
    {/* Head - the animated part */}
    <path
      className={styles.spinnerHead}
      d="M12 2C6.47715 2 2 6.47715 2 12"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Button Component
 *
 * A highly customizable button component supporting
 * - Multiple varients (solid,outline,ghost)
 * - Multiple colors (primary(orange),secandary(yellow))
 * - Multiple sizes(xs,sm,md,lg,xl)
 * - Icons (left,right or icon only )
 * - Loading and disabled states
 * - Custom colors via props
 *
 */

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      // Children props
      children, // Button text/content
      leftIcon,
      rightIcon,
      iconOnly = false,

      // Styling props
      varient = "solid",
      color = "primary",
      size = "md",
      fullWidth = false,
      rounded = false,

      // State props
      isLoading = false,
      isDisabled = false,
      isActive = false,

      // custom color properties
      bgColor,
      textColor,
      borderColor,
      hoverBgColor,

      // Html attributes
      className,
      type = "button",
      ...rest
    },
    ref,
  ) => {
    /**
     * Computed Values
     *
     * Determine if button shoul be disabled
     * Button is disabled when:
     *  - isDisabled prop is true
     *  - isLoading prop is true(prevent double clidks)
     */
    const disabled = isDisabled || isLoading;

    /**
     * Css Custom Properties for custom colors
     * Only included if the prop is provided
     *
     * Why css variables?
     * - Can be changed at runtime
     * - Can be overridden by specificity
     * - Keeps the css clean and maintainable
     */
    const customStyles = {
      // Only add property if value exists
      ...(bgColor && { "--btn-bg": bgColor }),
      ...(textColor && { "--btn-text": textColor }),
      ...(borderColor && { "--btn-border": borderColor }),
      ...(hoverBgColor && { "--btn-hover-bg": hoverBgColor }),
    } as React.CSSProperties;

    /**
     * Check if custom styles are provided
     * User to conditionally add the hasCustomStyles class
     */
    const hasCustomStyles = bgColor || textColor || borderColor || hoverBgColor;

    //=============================================
    // Class Names
    // Using clsx for conditional class composition
    //=============================================
    /**
     * Build the cutton's class list
     *
     * Class Structure
     * 1. Base class - always applied
     * 2. Varient + Color - e.g 'solid-priamry'
     * 3. Size - e.g 'size-md'
     * 4. State classes - loading,active,disabled
     * 5. Modifier Classes - fullWidth,rounded,iconOnly
     * 6. Custom styles flag
     * 7- External classname
     */
    const buttonClasses = clsx(
      // 1. Base style always applied
      styles.button,
      //2. varient + color
      styles[`${varient}-${color}`],
      // 3. size varieant
      styles[`size-${size}`],
      // 4. State classes consditional
      {
        [styles.isLoading]: isLoading,
        [styles.isActive]: isActive,
        [styles.isDisabled]: disabled,
      },
      //5. Modifier classes(conditional)
      {
        [styles.fullWidth]: fullWidth,
        [styles.rounded]: rounded,
        [styles.iconOnly]: iconOnly,
      },
      // 6.Custom styles flag
      {
        [styles.hasCustomStyles]: hasCustomStyles,
      },
      //7.External className (passsed as properties)
      className,
    );
    /**
     * Icon class Names
     */
    const leftIconClasses = clsx(styles.icon, styles.leftIcon);
    const rightIconClasses = clsx(styles.icon, styles.rightIcon);

    return (
      <button
        // Forward ref to parent
        ref={ref}
        //Button type (prevents for submission by default)
        type={type}
        //Combine Class names
        className={buttonClasses}
        //Disabled
        disabled={disabled}
        //Accessibility attributes
        aria-disabled={disabled} // for screen reader
        aria-busy={isLoading}
        // Custom styles (only if provided)
        style={hasCustomStyles ? customStyles : undefined}
        // Spread remaining props
        {...rest}
      >
        {/**
         * Loading Spinner
         * Shown when isLoading is true
         * Replaces the left icon position
         */}
        {isLoading && <LoadingSpinner className={styles.loadingIcon} />}
        {/**
         * Left Icon only shows when
         * - left icon props is provided
         * - Not in loading state
         *
         */}

        {!isLoading && leftIcon && (
          <span className={leftIconClasses}>{leftIcon}</span>
        )}
        {/**
         * Button content
         * Main text/content of the button
         * When iconOnly is true content is visually hidden
         * but still accessible to screen readers
         */}
        <span className={styles.content}>{children}</span>

        {/**
         * Right Icon Only shows when
         * -rightIcon prop is provided
         * -Not in loading state
         */}
        {rightIcon && !isLoading && (
          <span className={rightIconClasses}>{rightIcon}</span>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";
